import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  encargosDaFatura,
  FORMAS_DO_RECEBIMENTO,
  ROTULO_FORMA_PAGAMENTO,
  textoDoPercentual,
  zRecebimento,
  type DetalheDaFatura,
  type FormaDoRecebimento,
} from '@arena/shared/schemas';
import { dataValida } from '@arena/shared/tempo';
import { dataCurta, dinheiro } from '../agenda/formatos.js';
import { errosPorCampo, primeiraMensagem } from '../alunos/formatos.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { cobrancasApi } from '../api/planos.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario, Mini, Nota, Status } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { competenciaPorExtenso, quemPaga, rotuloDaFatura, SITUACAO_DA_FATURA } from './formatos.js';

/**
 * `#m-pagamento` — registrar o que já aconteceu.
 *
 * O total é calculado AQUI, a cada data escolhida, pela mesma função que o
 * servidor usa (`encargosDaFatura`) e com a regra da arena que veio junto
 * da fatura. Confirmar manda o total que a tela mostrou: se a
 * configuração de multa mudou no meio do caminho, o servidor recusa em
 * vez de cobrar um número que ninguém viu.
 *
 * "Cobrar multa e juros" vem marcado e pode ser desmarcado. Desmarcar é
 * renúncia — o servidor grava quem, quando e quanto, e a nota diz isso
 * antes de a pessoa confirmar.
 *
 * Numa fatura paga ou cancelada o modal vira a ficha dela, com as duas
 * correções que existem, cada uma para quem a matriz deixa: desfazer o
 * recebimento (o razão estorna) e cancelar a fatura.
 */
export function ModalDePagamento({
  faturaId,
  onFechar,
}: {
  faturaId: string;
  onFechar: () => void;
}) {
  const consulta = useQuery({
    queryKey: ['cobrancas', 'fatura', faturaId],
    queryFn: () => cobrancasApi.fatura(faturaId),
    retry: false,
  });

  if (consulta.isPending) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Registrar pagamento" largura="estreita">
        <CorpoDeModal>
          <EsqueletoDeCard linhas={4} />
        </CorpoDeModal>
      </Modal>
    );
  }
  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Modal aberto onFechar={onFechar} titulo="Fatura" largura="estreita">
        <CorpoDeModal>
          <Erro
            tipo={
              erro instanceof ErroDeRede
                ? 'rede'
                : erro instanceof ErroDeApi && erro.ehPermissao
                  ? 'permissao'
                  : 'falha'
            }
            detalhe={mensagemDoErro(erro)}
          />
        </CorpoDeModal>
      </Modal>
    );
  }

  return <PagamentoDaFatura fatura={consulta.data} onFechar={onFechar} />;
}

function PagamentoDaFatura({
  fatura,
  onFechar,
}: {
  fatura: DetalheDaFatura;
  onFechar: () => void;
}) {
  const { pode } = useSessao();
  const avisar = useToast();
  const cliente = useQueryClient();

  const aberta = fatura.situacao === 'EmAberto' || fatura.situacao === 'Vencida';
  const podeReceber = aberta && pode('cobrancas', 'registrar_pagamento');

  const [forma, setForma] = useState<FormaDoRecebimento>('Pix');
  const [data, setData] = useState(fatura.hoje);
  const [cobrarEncargos, setCobrarEncargos] = useState(true);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);

  // A conta da tela — a mesma do servidor. Data inválida ou no futuro
  // mostra a de hoje: o servidor recusa as duas, com a mensagem certa.
  const dataDaConta = dataValida(data) && data <= fatura.hoje ? data : fatura.hoje;
  const encargos = encargosDaFatura(fatura.valor, fatura.vencimento, dataDaConta, fatura.regra);
  const renuncia = !cobrarEncargos && encargos.encargos > 0;
  const total = renuncia ? fatura.valor : encargos.total;

  const invalidar = () =>
    Promise.all([
      cliente.invalidateQueries({ queryKey: ['cobrancas'] }),
      cliente.invalidateQueries({ queryKey: ['financeiro'] }),
      cliente.invalidateQueries({ queryKey: ['planos'] }),
      cliente.invalidateQueries({ queryKey: ['turmas', 'risco'] }),
    ]);

  const receber = useMutation({
    mutationFn: (corpo: Parameters<typeof cobrancasApi.receber>[1]) =>
      cobrancasApi.receber(fatura.id, corpo),
    onSuccess: invalidar,
  });

  async function confirmar() {
    setErros({});
    setErroGeral(null);
    const pedido = zRecebimento.safeParse({
      data,
      formaPagamento: forma,
      cobrarEncargos,
      totalEsperado: total,
    });
    if (!pedido.success) {
      setErros(errosPorCampo(pedido.error.issues));
      return;
    }
    try {
      await receber.mutateAsync(pedido.data);
      avisar(`Pagamento registrado. Fatura de ${fatura.titular.nome.split(' ')[0]} quitada.`);
      onFechar();
    } catch (erro) {
      if (erro instanceof ErroDeApi && erro.campos) setErros(primeiraMensagem(erro.campos));
      setErroGeral(mensagemDoErro(erro));
    }
  }

  const vencimentoTexto =
    fatura.situacao === 'Vencida'
      ? `vencida em ${dataCurta(fatura.vencimento)}`
      : `vence em ${dataCurta(fatura.vencimento)}`;

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={
        podeReceber
          ? 'Registrar pagamento'
          : `Fatura de ${competenciaPorExtenso(fatura.competencia)}`
      }
      descricao={`${quemPaga(fatura)} · ${fatura.plano.nome} · ${vencimentoTexto}`}
      largura="estreita"
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            {podeReceber ? 'Cancelar' : 'Fechar'}
          </Botao>
          {podeReceber && (
            <Botao onClick={() => void confirmar()} disabled={receber.isPending}>
              {receber.isPending ? 'Registrando…' : 'Confirmar recebimento'}
            </Botao>
          )}
        </>
      }
    >
      <CorpoDeModal>
        {podeReceber ? (
          <>
            <Mini
              itens={[
                { valor: dinheiro(fatura.valor), rotulo: 'valor original' },
                {
                  valor: dinheiro(renuncia ? 0 : encargos.encargos),
                  rotulo:
                    encargos.diasDeAtraso > 0
                      ? `multa e juros · ${encargos.diasDeAtraso} ${encargos.diasDeAtraso === 1 ? 'dia' : 'dias'}`
                      : 'multa e juros',
                },
                { valor: dinheiro(total), rotulo: 'total' },
              ]}
            />
            <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
              {/* As DUAS datas, lado a lado. O vencimento é do contrato e
                  não se edita aqui; a data do pagamento é o dia em que o
                  dinheiro entrou, e é ela que decide multa e juros. Sem o
                  vencimento à vista, quem registra um pagamento atrasado
                  não tem como conferir contra o que a tela cobrou. */}
              <Campo rotulo="Vencimento" ajuda="Do contrato — muda na assinatura.">
                <input type="date" value={fatura.vencimento} readOnly disabled />
              </Campo>
              <Campo
                rotulo="Data do pagamento"
                ajuda={
                  encargos.diasDeAtraso > 0
                    ? `${encargos.diasDeAtraso} ${encargos.diasDeAtraso === 1 ? 'dia' : 'dias'} depois do vencimento`
                    : 'No prazo.'
                }
                erro={erros['data']}
              >
                <input
                  type="date"
                  value={data}
                  max={fatura.hoje}
                  onChange={(e) => setData(e.target.value)}
                />
              </Campo>
              <Campo rotulo="Forma" erro={erros['formaPagamento']}>
                <select
                  value={forma}
                  onChange={(e) => setForma(e.target.value as FormaDoRecebimento)}
                >
                  {FORMAS_DO_RECEBIMENTO.map((f) => (
                    <option key={f} value={f}>
                      {ROTULO_FORMA_PAGAMENTO[f]}
                    </option>
                  ))}
                </select>
              </Campo>
            </Formulario>

            <label className="consent">
              <input
                className="ck"
                type="checkbox"
                checked={cobrarEncargos}
                onChange={(e) => setCobrarEncargos(e.target.checked)}
              />
              <span>
                Cobrar multa e juros
                <small>
                  {textoDoPercentual(Math.round(fatura.regra.multaPercentual * 100))} de multa e{' '}
                  {textoDoPercentual(Math.round(fatura.regra.jurosMesPercentual * 100))} ao mês,
                  conforme configuração
                </small>
              </span>
            </label>
            {renuncia && (
              <Nota>
                Renúncia de <b>{dinheiro(encargos.encargos)}</b> em multa e juros. Fica registrado
                quem renunciou, quando e quanto.
              </Nota>
            )}
            {erroGeral && (
              <div className="aviso conflito" role="alert">
                <Icone nome="info" />
                <div>{erroGeral}</div>
              </div>
            )}
          </>
        ) : (
          <FichaDaFatura fatura={fatura} />
        )}

        <CorrecoesDaFatura fatura={fatura} onFeito={onFechar} invalidar={invalidar} />
      </CorpoDeModal>
    </Modal>
  );
}

/** A fatura que não recebe mais: paga, cancelada — ou aberta para quem só lê. */
function FichaDaFatura({ fatura }: { fatura: DetalheDaFatura }) {
  return (
    <>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <Status situacao={SITUACAO_DA_FATURA[fatura.situacao]}>{rotuloDaFatura(fatura)}</Status>
        <span className="sub">{competenciaPorExtenso(fatura.competencia)}</span>
      </div>
      <Mini
        itens={
          fatura.situacao === 'Paga'
            ? [
                { valor: dinheiro(fatura.valor), rotulo: 'valor' },
                {
                  valor: dinheiro((fatura.multa ?? 0) + (fatura.juros ?? 0)),
                  rotulo: 'multa e juros',
                },
                { valor: dinheiro(fatura.valorPago ?? fatura.valor), rotulo: 'pago' },
              ]
            : [
                { valor: dinheiro(fatura.valor), rotulo: 'valor' },
                { valor: dinheiro(fatura.encargosHoje.encargos), rotulo: 'encargos hoje' },
                { valor: dinheiro(fatura.encargosHoje.total), rotulo: 'total hoje' },
              ]
        }
      />
      <dl className="detalhes">
        <dt>Vencimento</dt>
        <dd>{dataCurta(fatura.vencimento)}</dd>
        <dt>Pagamento</dt>
        <dd>
          {fatura.pagoEm ? (
            <>
              {dataCurta(fatura.pagoEm)}
              {fatura.diasDeAtraso > 0
                ? ` · ${fatura.diasDeAtraso} ${fatura.diasDeAtraso === 1 ? 'dia' : 'dias'} depois do vencimento`
                : ' · no prazo'}
            </>
          ) : (
            'ainda não recebida'
          )}
        </dd>
      </dl>
      {fatura.situacao === 'Paga' && fatura.pagoEm && (
        <p className="sub">
          {fatura.formaPagamento ? ROTULO_FORMA_PAGAMENTO[fatura.formaPagamento] : '—'}
          {fatura.recebida?.nome ? ` · registrada por ${fatura.recebida.nome}` : ''}
        </p>
      )}
      {fatura.encargosRenunciados !== null && (
        <Nota>
          Renúncia de <b>{dinheiro(fatura.encargosRenunciados)}</b> em multa e juros
          {fatura.renuncia?.nome ? `, por ${fatura.renuncia.nome}` : ''}.
        </Nota>
      )}
      {fatura.situacao === 'Cancelada' && (
        <Nota>
          Cancelada{fatura.cancelada?.nome ? ` por ${fatura.cancelada.nome}` : ''}:{' '}
          {fatura.motivoCancelamento}
        </Nota>
      )}
      {fatura.alunos.length > 1 && (
        <p className="sub">Cobre {fatura.alunos.map((a) => a.nome).join(', ')}.</p>
      )}
    </>
  );
}

/**
 * As duas correções: desfazer o recebimento (o razão estorna na origem) e
 * cancelar a fatura. Cada uma pede motivo, e cada uma aparece só para quem
 * a matriz deixa — o servidor confere de novo.
 */
function CorrecoesDaFatura({
  fatura,
  onFeito,
  invalidar,
}: {
  fatura: DetalheDaFatura;
  onFeito: () => void;
  invalidar: () => Promise<unknown>;
}) {
  const { pode } = useSessao();
  const avisar = useToast();
  const [acao, setAcao] = useState<'estornar' | 'cancelar' | null>(null);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const podeEstornar = fatura.situacao === 'Paga' && pode('cobrancas', 'editar');
  const podeCancelar =
    (fatura.situacao === 'EmAberto' || fatura.situacao === 'Vencida') &&
    pode('cobrancas', 'cancelar');

  const corrigir = useMutation({
    mutationFn: () =>
      acao === 'estornar'
        ? cobrancasApi.estornar(fatura.id, { motivo })
        : cobrancasApi.cancelar(fatura.id, { motivo }),
    onSuccess: invalidar,
  });

  if (!podeEstornar && !podeCancelar) return null;

  if (!acao) {
    return (
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {podeEstornar && (
          <Botao variante="contorno" pequeno onClick={() => setAcao('estornar')}>
            Desfazer recebimento
          </Botao>
        )}
        {podeCancelar && (
          <Botao variante="contorno" pequeno onClick={() => setAcao('cancelar')}>
            Cancelar fatura
          </Botao>
        )}
      </div>
    );
  }

  async function confirmar() {
    setErro(null);
    try {
      await corrigir.mutateAsync();
      avisar(
        acao === 'estornar'
          ? 'Recebimento desfeito. O razão recebeu o estorno.'
          : 'Fatura cancelada. A linha fica, com o motivo.',
      );
      onFeito();
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 12,
        background: 'var(--bg)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-sm)',
      }}
    >
      <Campo
        rotulo={
          acao === 'estornar' ? 'Por que desfazer o recebimento?' : 'Por que cancelar a fatura?'
        }
        erro={erro ?? undefined}
      >
        <input value={motivo} onChange={(e) => setMotivo(e.target.value)} />
      </Campo>
      <div style={{ display: 'flex', gap: 8 }}>
        <Botao
          variante="fantasma"
          pequeno
          onClick={() => {
            setAcao(null);
            setMotivo('');
            setErro(null);
          }}
        >
          Voltar
        </Botao>
        <Botao pequeno onClick={() => void confirmar()} disabled={corrigir.isPending}>
          {corrigir.isPending ? 'Salvando…' : acao === 'estornar' ? 'Desfazer' : 'Cancelar fatura'}
        </Botao>
      </div>
    </div>
  );
}
